/**
 * T2 Business Dashboard — the two supplier leaderboards.
 *
 * Laid out as the supplied design reference draws them (Supply Chain Dashboard v5): two panels on a
 * hairline grid, a micro uppercase column strip, then ranked rows — rank, supplier with a material
 * class flag, an SDP track, the measure, orders late and average delay.
 *
 * A **ranked panel, not a `<DataTable>`**: this is five rows of several hundred chosen by the
 * server, the same shape `sewing-efficiency`'s `TopBottomLines` uses, and the project's table chrome
 * would fight the reference's layout. No raw `<table>` either (§4.3) — an ordered list of rows.
 *
 * **No totals row**, a documented exception to the project default: a board is a deliberate
 * five-row slice of several hundred suppliers, so a total would present a partial figure as a whole
 * one, and SDP is a ratio that cannot be summed down a column regardless.
 *
 * Ranking is the query's job. The best five come off the server's order; the worst five come from
 * its `rank_worst`, because one `ORDER BY` cannot put the largest supplier first at both ends of an
 * SDP tie and reading the order backwards surfaced a 69-unit supplier as the worst in the business.
 * Ordering five loaded rows is presentation; which five they are was decided in SQL.
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';

import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

import {
  SDP_TARGET_PCT,
  measureFigure,
  measureLabel,
  meetsTarget,
  pct,
  widthPct,
  type SupplierSdpRow,
} from './contract';

const BOARD_SIZE = 5;

/** The reference's row grid, shared by the column strip and every row so they stay aligned. */
const ROW_GRID =
  'grid grid-cols-[20px_minmax(0,1fr)_minmax(80px,150px)_76px_50px_74px] items-center gap-2.5';

/** The reference's micro column label. */
const COL = 'text-[10px] uppercase tracking-[0.12em] text-t2d-faint';

/**
 * The reference flags a supplier by what it sells. Identity, not grading — the colour says fabric
 * or trims, never how the supplier is doing.
 */
function classFlag(materialClass: string | null): string {
  if (materialClass === 'Fabric') return 'bg-t2d-good';
  if (materialClass === 'Accessories') return 'bg-t2d-blue';
  return 'bg-t2d-violet';
}

/**
 * Average delay against the tolerance that already exists: SDP counts a delivery on time from early
 * through 3 days late, so a mean beyond 3 days is beyond the rule the rest of the report runs on
 * and a negative mean is a supplier arriving ahead of its promised date. Both boundaries are the
 * transactional report's, not invented here — the reference's second band at 7 days has nothing
 * behind it and is deliberately not drawn.
 */
function delayInk(days: number | null): string {
  if (typeof days !== 'number' || !Number.isFinite(days)) return 'text-t2d-faint';
  if (days < 0) return 'text-t2d-good-ink';
  return days > 3 ? 'text-t2d-bad-ink' : 'text-t2d-ink';
}

function Row({ row, rank }: { row: SupplierSdpRow; rank: number }) {
  const met = meetsTarget(row.sdp_pct, row.sdp_on_time_qty, row.sdp_delivered_qty);

  return (
    <li className={cn(ROW_GRID, 'border-t border-t2d-line-soft py-[7px]')}>
      <span className="text-xs text-t2d-faint tabular-nums">{rank}</span>

      <div className="flex min-w-0 items-center gap-2">
        <span aria-hidden className={cn('h-[15px] w-[3px] shrink-0', classFlag(row.material_class))} />
        <span className="flex min-w-0 flex-col leading-tight">
          <span className="truncate text-[13.5px] font-medium text-t2d-ink">
            {row.t2_group ?? '—'}
          </span>
          <span className="truncate text-[11px] text-t2d-faint">
            {row.material_class ?? '—'} · {row.factory_count ?? 0} factories
          </span>
        </span>
      </div>

      <div className="flex items-center gap-2">
        <div className="h-2 min-w-8 flex-1 bg-t2d-track">
          {/* The one inline style §4.8 allows: a runtime-computed width. */}
          <div
            className={cn('h-full', met ? 'bg-t2d-good' : 'bg-t2d-ink/55')}
            style={{ width: widthPct(row.sdp_pct) }}
          />
        </div>
        <span
          className={cn(
            'w-11 shrink-0 text-right text-[13.5px] font-semibold tabular-nums',
            met ? 'text-t2d-good-ink' : 'text-t2d-ink',
          )}
        >
          {pct(row.sdp_pct, 0)}
        </span>
      </div>

      <span className="text-right text-[13.5px] font-medium text-t2d-ink tabular-nums">
        {measureFigure(row.measure_value)}
      </span>
      <span className="text-right text-[13px] text-t2d-muted tabular-nums">
        {row.pos_late ?? 0}
      </span>
      <span
        className={cn('text-right text-[13px] font-medium tabular-nums', delayInk(row.avg_delay_days))}
      >
        {typeof row.avg_delay_days === 'number' ? `${row.avg_delay_days.toFixed(1)}d` : '—'}
      </span>
    </li>
  );
}

function Board({
  title,
  subLine,
  accent,
  rows,
  ranks,
  measure,
  emptyMessage,
}: {
  title: string;
  subLine: string;
  accent: string;
  rows: readonly SupplierSdpRow[];
  /** Each row's position in the full ranking, so the worst board does not read as 1–5. */
  ranks: readonly number[];
  measure: string;
  emptyMessage: string;
}) {
  return (
    <section aria-label={title} className="flex min-w-0 flex-col px-6 pt-4 pb-4.5">
      <div className="flex items-baseline gap-2.5">
        <h3 className={cn('text-[15px] font-semibold', accent)}>{title}</h3>
        <span className="text-xs text-t2d-faint">{subLine}</span>
      </div>

      <div className={cn(ROW_GRID, 'pt-2.5 pb-1', COL)}>
        <span />
        <span>Supplier</span>
        <span>SDP · target {pct(SDP_TARGET_PCT, 0)}</span>
        <span className="text-right">{measureLabel(measure)}</span>
        <span className="text-right">Late</span>
        <span className="text-right">Avg delay</span>
      </div>

      {rows.length === 0 ? (
        <p className="py-4 text-sm text-t2d-muted">{emptyMessage}</p>
      ) : (
        <ol className="flex flex-col">
          {rows.map((row, index) => (
            <Row key={row.t2_group ?? index} row={row} rank={ranks[index] ?? index + 1} />
          ))}
        </ol>
      )}
    </section>
  );
}

export function SupplierBoards({
  rows,
  measure,
  complete,
  isLoading,
}: {
  rows: readonly SupplierSdpRow[];
  measure: string;
  /**
   * The response was not capped. Taking the two ends of a ranking is a whole-result claim (§4.3):
   * over a truncated result they would be the best and worst of what loaded.
   */
  complete: boolean;
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <div className="grid border-t border-t2d-line lg:grid-cols-2">
        {[0, 1].map((panel) => (
          <div key={panel} className="flex flex-col gap-2 border-t2d-line px-6 py-4 lg:not-first:border-l">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-28 w-full" />
          </div>
        ))}
      </div>
    );
  }

  if (!complete) {
    return (
      <p className="flex items-start gap-2 border-t border-t2d-line px-6 py-4 text-sm text-t2d-muted">
        <FontAwesomeIcon
          icon={faTriangleExclamation}
          className="mt-0.5 size-3.5 shrink-0 text-t2d-gold-ink"
          aria-hidden
        />
        The supplier ranking came back incomplete, so the best and worst five of it would be the best
        and worst of the rows that loaded rather than of the business. Narrow the dates, or pick a
        material type or a season, and the boards come back.
      </p>
    );
  }

  const top = rows.slice(0, BOARD_SIZE);
  const topRanks = top.map((_, index) => index + 1);

  // Never the same supplier on both boards: with fewer than ten ranked suppliers the bottom board
  // gives up its rows rather than repeating ones the top board already showed as the best.
  const onTopBoard = new Set(top.map((row) => row.t2_group));
  const bottom = rows
    .filter((row) => !onTopBoard.has(row.t2_group) && typeof row.rank_worst === 'number')
    .sort((a, b) => (a.rank_worst ?? 0) - (b.rank_worst ?? 0))
    .slice(0, BOARD_SIZE)
    .reverse();
  const bottomRanks = bottom.map((row) => rows.length - (row.rank_worst ?? 1) + 1);

  return (
    <div className="grid border-t border-t2d-line lg:grid-cols-2">
      <Board
        title="Top 5 suppliers"
        subLine="highest SDP"
        accent="text-t2d-good-ink"
        rows={top}
        ranks={topRanks}
        measure={measure}
        emptyMessage="No supplier delivered anything in this period. Widen the dates, or clear a filter."
      />
      <div className="border-t border-t2d-line lg:border-t-0 lg:border-l">
        <Board
          title="Bottom 5 suppliers"
          subLine="lowest SDP"
          accent="text-t2d-bad-ink"
          rows={bottom}
          ranks={bottomRanks}
          measure={measure}
          emptyMessage="Every ranked supplier is already on the top board — there are fewer than ten with deliveries in this period."
        />
      </div>
    </div>
  );
}
